import { ERROR_RECOGNITION_MODE, LevelData } from 'Types';
export type UserRepertoireData = {
  id: number;
  name: string;
  user_id: string;
  bpm?: number;
  level?: LevelData | null;
  genres?: Genre[];
  composers: Composer[];
  artists: Artist[];
  arrangers: Arranger[];
  year?: number;
  status: 'processing' | 'processed' | 'complete' | 'failed' | 'error';
  source?: 'upload' | 'created';
  repertoire_png_preview_url?: string;
  created_at: string;
  updated_at: string;
};

export enum RepertoireActionTypes {
  UPDATE_REPERTOIRE_DATA_REQUEST = 'UPDATE_REPERTOIRE_DATA_REQUEST',
  FETCH_REPERTOIRE_DATA_SUCCESS = 'FETCH_REPERTOIRE_DATA_SUCCESS',
  UPDATE_REPERTOIRE_DATA_LOCAL = 'UPDATE_REPERTOIRE_DATA_LOCAL',
  SET_REPERTOIRE_ENDED = 'SET_REPERTOIRE_ENDED',
  SET_REPERTOIRE_SELECT = 'SET_REPERTOIRE_SELECT',
  FETCH_URP_SUCCESS = 'FETCH_URP_SUCCESS',
  LOAD_REPERTOIRE_STATE = 'LOAD_REPERTOIRE_STATE',
  CREATE_REPERTOIRE_SESSION = 'CREATE_REPERTOIRE_SESSION',
  CREATE_REPERTOIRE_SESSION_SUCCESS = 'CREATE_REPERTOIRE_SESSION_SUCCESS',
  SET_REPERTOIRE_PLAYING = 'SET_REPERTOIRE_PLAYING',
  SET_PLAYBACK_PHASE = 'SET_PLAYBACK_PHASE',
  SET_PLAY_SESSION_ID = 'SET_PLAY_SESSION_ID',
  SET_CURSOR_VISIBLE = 'SET_CURSOR_VIBLE',
  FETCH_USER_REPERTOIRE_SUCCESS = 'FETCH_USER_REPERTOIRE_SUCCESS',
  SET_USER_REPERTOIRE_PROCESSING = 'SET_USER_REPERTOIRE_PROCESSING',
  SET_VERTICAL_SCROLL = 'SET_VERTICAL_SCROLL',
  SET_HORIZONTAL_SCROLL = 'SET_HORIZONTAL_SCROLL',
}

export type Genre = {
  id: number;
  name: string;
};
export type Composer = {
  id: number;
  name: string;
};
export type Artist = {
  id: number;
  name: string;
};
export type Arranger = {
  id: number;
  name: string;
};

export type RepertoireData = {
  id: number;
  name: string;
  bpm: number;
  level: LevelData;
  genres: Genre[];
  artists: Artist[];
  arranger: Arranger[];
  composers: Composer[];
  repertoire_png_preview_url: string;
  owner?: string;
  status?: 'processing' | 'processed' | 'complete' | 'failed' | 'error';
  source?: 'upload' | 'created';
};

export type RepertoireDataId = {
  id: number;
};

export type UserRepertoireProgress = {
  id?: number;
  user_id?: string;
  status: string;
  repertoire: RepertoireDataId | null;
  last_played_at: string;
  completed_at: string | null;
  accuracy: number | null;
  percent_complete: number | null;
  last_tempo?: number | null | undefined;
};

export interface SetRepertoireSelectPayload {
  repertoireIdSelect: number;
}

export type RepertoireSessionPayload = {
  repertoire_id: number;
  error_recognition_mode: ERROR_RECOGNITION_MODE;
};

export enum actionTypes {
  CREATE_REPERTOIRE_SESSION = 'CREATE_REPERTOIRE_SESSION',
  UPDATE_REPERTOIRE_DATA_REQUEST = 'UPDATE_REPERTOIRE_DATA_REQUEST',
  FETCH_REPERTOIRE_DATA_SUCCESS = 'FETCH_REPERTOIRE_DATA_SUCCESS',
  UPDATE_REPERTOIRE_DATA_LOCAL = 'UPDATE_REPERTOIRE_DATA_LOCAL',
  SET_REPERTOIRE_ENDED = 'SET_REPERTOIRE_ENDED',
  SET_REPERTOIRE_SELECT = 'SET_REPERTOIRE_SELECT',
  FETCH_URP_SUCCESS = 'FETCH_URP_SUCCESS',
  LOAD_REPERTOIRE_STATE = 'LOAD_REPERTOIRE_STATE',
  SET_CURSOR_VISIBLE = 'SET_CURSOR_VISIBLE',
}

export interface SetRepertoirePlayingPayload {
  repertoirePlaying: boolean;
}

/** Mirrors the Phaser-owned playback state machine's current phase.
 *  MUS-243: Redux is a read-only projection of the machine; UI components
 *  should read playback state via this field or its derived selectors. */
export type PlaybackPhase =
  | 'stopped'
  | 'normalCountIn'
  | 'loopCountInWait'
  | 'playing'
  | 'paused'
  | 'loopBoundaryRestart';

export interface SetPlaybackPhasePayload {
  phase: PlaybackPhase;
  isLoopingActive: boolean;
  hasEverPlayedInThisLoop: boolean;
}

export type CreateRepertoireSession = {
  type: typeof RepertoireActionTypes.CREATE_REPERTOIRE_SESSION;
  payload: RepertoireSessionPayload;
};

export type CreateRepertoireSessionSuccess = {
  type: typeof RepertoireActionTypes.CREATE_REPERTOIRE_SESSION_SUCCESS;
};

export enum REPERTOIRE_PROGRESS {
  COMPLETE = 'complete',
  IN_PROGRESS = 'in progress',
  NOT_STARTED = 'not started',
}

export interface ScrollPositions {
  home: number;
  browse: number;
}

export interface HorizontalScrollPositions {
  [rowId: string]: number;
}

export interface RepertoireState {
  repertoireData?: RepertoireData[];
  userRepertoireProgresses: UserRepertoireProgress[];
  userRepertoireData: UserRepertoireData[];
  repertoireIdSelect: number;
  /** @deprecated MUS-243: derive from `playbackPhase` via
   *  `selectIsRepertoirePlaying`. Still mutated by legacy callers but
   *  destined for removal. */
  repertoirePlaying: boolean;
  /** MUS-243: mirror of the Phaser playback machine's current phase. */
  playbackPhase: PlaybackPhase;
  isLoopingActiveFromMachine: boolean;
  hasEverPlayedInThisLoop: boolean;
  repertoireEnded: boolean;
  cursorVisible: boolean;
  playSessionId: string | null;
  verticalScrollPositions: ScrollPositions;
  horizontalScrollPositions: HorizontalScrollPositions;
}

/** Derived selector: true when the playback machine is actively producing
 *  sound / advancing the cursor. Use this for "is the metronome / cursor
 *  running right now" — note playback enable, accuracy loop gating. */
export const selectIsRepertoirePlaying = (state: RepertoireReducer): boolean =>
  state.repertoireReducer.playbackPhase === 'playing';

/** Derived selector: true when the user has initiated playback and the
 *  machine has not been paused or stopped. This covers count-in wait, the
 *  loop boundary restart hop, and active playing. Use for play/pause button
 *  iconography — the user expects the pause icon as soon as they click play,
 *  even while waiting for the first note during count-in. */
export const selectIsPlaybackActive = (state: RepertoireReducer): boolean => {
  const phase = state.repertoireReducer.playbackPhase;
  return phase !== 'stopped' && phase !== 'paused';
};

export const selectIsLoopCountInActive = (state: RepertoireReducer): boolean =>
  state.repertoireReducer.playbackPhase === 'loopCountInWait';

export const selectIsPaused = (state: RepertoireReducer): boolean =>
  state.repertoireReducer.playbackPhase === 'paused';

export const selectPlaybackPhase = (state: RepertoireReducer): PlaybackPhase =>
  state.repertoireReducer.playbackPhase;

export type UpdateRepertoireDataRequest = {
  type: typeof RepertoireActionTypes.UPDATE_REPERTOIRE_DATA_REQUEST;
  payload: RepertoireData;
};

export type FetchRepertoireDataSuccess = {
  type: typeof RepertoireActionTypes.FETCH_REPERTOIRE_DATA_SUCCESS;
  payload: RepertoireData[];
};

export type FetchRepertoireDataLocal = {
  type: typeof RepertoireActionTypes.UPDATE_REPERTOIRE_DATA_LOCAL;
  payload: Partial<UserRepertoireProgress>;
};

export type SetRepertoireEnded = {
  type: typeof RepertoireActionTypes.SET_REPERTOIRE_ENDED;
  payload: boolean;
};

export type SetRepertoireSelect = {
  type: typeof RepertoireActionTypes.SET_REPERTOIRE_SELECT;
  payload: SetRepertoireSelectPayload;
};

export type FetchURPSuccess = {
  type: typeof RepertoireActionTypes.FETCH_URP_SUCCESS;
  payload: UserRepertoireProgress[];
};

export type LoadRepertoireState = {
  type: typeof RepertoireActionTypes.LOAD_REPERTOIRE_STATE;
  payload: RepertoireData;
};

export type SetRepertoirePlaying = {
  type: typeof RepertoireActionTypes.SET_REPERTOIRE_PLAYING;
  payload: SetRepertoirePlayingPayload;
};

export type SetPlaybackPhase = {
  type: typeof RepertoireActionTypes.SET_PLAYBACK_PHASE;
  payload: SetPlaybackPhasePayload;
};

export type SetPlaySessionId = {
  type: typeof RepertoireActionTypes.SET_PLAY_SESSION_ID;
  payload: string | null;
};

export type SetCursorVisible = {
  type: typeof RepertoireActionTypes.SET_CURSOR_VISIBLE;
  payload: boolean;
};

export type FetchUserRepertoireSuccess = {
  type: typeof RepertoireActionTypes.FETCH_USER_REPERTOIRE_SUCCESS;
  payload: UserRepertoireData[];
};

export type SetUserRepertoireProcessing = {
  type: typeof RepertoireActionTypes.SET_USER_REPERTOIRE_PROCESSING;
  payload: { id: number; status: 'processing' | 'processed' | 'complete' | 'failed' | 'error' };
};

export type SetVerticalScroll = {
  type: typeof RepertoireActionTypes.SET_VERTICAL_SCROLL;
  payload: { page: 'home' | 'browse'; position: number };
};

export type SetHorizontalScroll = {
  type: typeof RepertoireActionTypes.SET_HORIZONTAL_SCROLL;
  payload: { rowId: string; position: number };
};

export interface RepertoireReducer {
  repertoireReducer: RepertoireState;
}

export type RepertoireActions =
  | UpdateRepertoireDataRequest
  | FetchRepertoireDataSuccess
  | FetchRepertoireDataLocal
  | FetchURPSuccess
  | SetRepertoireEnded
  | SetRepertoireSelect
  | LoadRepertoireState
  | CreateRepertoireSession
  | CreateRepertoireSessionSuccess
  | SetRepertoirePlaying
  | SetPlaybackPhase
  | SetPlaySessionId
  | SetCursorVisible
  | FetchUserRepertoireSuccess
  | SetUserRepertoireProcessing
  | SetVerticalScroll
  | SetHorizontalScroll;
